/**
 * 投稿詳細スコアセクション 評価ロジック (Sprint BG-DataSettings 18、GAP U-H1 close)。
 *
 * パイプライン (旧踏襲):
 *   抽出 → 抽出条件①(all/first) → 抽出条件②(filter) → 集約 or 式 → 整形
 *
 * 旧 `old/apps/user/src/lib/scoreEvaluator.ts` の核ロジックを v2 に踏襲。
 *
 * 踏襲点:
 *   - 4 種 extractor (extra-field / ai-aggregate / ai-classifications-score / ai-walker-overall)
 *   - filter 式パーサ (`>3` / `>=2` / `==1,3,5` / `5` 等の柔軟構文)
 *   - 5 種 aggregator (sum / avg / max / min / median)
 *   - formula 列 (`[label]` 参照 + 四則 + 括弧、安全な再帰下降パーサ、eval 不使用、0 除算 → null)
 *   - 2 段階解決 (aggregate 列 → label マップ → formula 列)
 *
 * 変更点 (v2):
 *   - 入力 `PostDoc` → `PostMinimal` (v2)
 *   - `AiAnalysis` 経路は `post.aiAnalysisBousai` / `post.aiAnalysisWalker` / `post.aiAnalysisEcw`
 *     にそれぞれ展開された v2 PostMinimal 構造から読む
 *   - ScoreColumnConfig / ScoreFieldDescriptor 型は shared-types 未昇格のため local 定義
 *
 * 廃止点:
 *   - 旧 `PostDoc.aiAnalysis` 直接アクセス (v2 PostMinimal は decomposed なので不要)
 *
 * 関連 docs: docs/91 U-H1 (score 評価ロジック)、docs/04 §3-1 M9。
 */
import type { PostMinimal } from '../posts';

// ============================================================
// Score config 型 (旧 shared-types から transplant、v2 minimum)
// ============================================================

export type ScoreAggregator = 'sum' | 'avg' | 'max' | 'min' | 'median';

export type ScoreExtractor =
  | { kind: 'extra-field'; key: string }
  | { kind: 'ai-aggregate'; modeKey: string; field: string; sub: string }
  | { kind: 'ai-classifications-score'; modeKey: string }
  /** @deprecated 【廃止予定 / legacy】walker 固定の総合スコア。新規は 'ai-scalar' (generic) を使う。 */
  | { kind: 'ai-walker-overall' }
  /**
   * MD-4 モードドリブン化 (2026-07-24): mode result 直下の scalar 数値 (例 aiAnalysis.generic の
   * M3 aggregateScores.overallKey)。`ai.generic.<key>` 形式の fieldPath から導出される。
   */
  | { kind: 'ai-scalar'; modeKey: string; field: string };

export interface ScoreFieldDescriptor {
  path: string;
  cardinality: 'scalar' | 'array';
  extractor: ScoreExtractor;
}

export interface ScoreColumnConfig {
  label: string;
  fieldPath: string;
  filter: { expr: string };
  selectionMode: 'all' | 'first';
  calculation:
    | { kind: 'aggregate'; aggregator: ScoreAggregator; multiplier?: number }
    | { kind: 'formula'; formula: string };
  decimals?: number;
  unit?: string;
}

// ----------------------------
// 抽出
// ----------------------------

function readModeResult(post: PostMinimal, modeKey: string): Record<string, unknown> | null {
  // v2 PostMinimal は aiAnalysisBousai / aiAnalysisWalker / aiAnalysisEcw / aiAnalysisGeneric に
  // decomposed。旧の `ai[modeKey]` 直接アクセスをこの helper で吸収。
  switch (modeKey) {
    // 【廃止予定 fallback】bousai/walker/ecw の mode 別 result は legacyModeResults 地区の投稿のみ。
    case 'bousai':
      return post.aiAnalysisBousai
        ? (post.aiAnalysisBousai as unknown as Record<string, unknown>)
        : null;
    case 'walker':
      return post.aiAnalysisWalker
        ? (post.aiAnalysisWalker as unknown as Record<string, unknown>)
        : null;
    case 'ecw':
      return post.aiAnalysisEcw ? (post.aiAnalysisEcw as unknown as Record<string, unknown>) : null;
    // MD-4 モードドリブン化 (2026-07-24): `ai.generic.<field>.<sub>` 形式の M5 スコア列が
    // 新規地区の集約スコア (aiAnalysis.generic 内、M3 aggregateScores キー) を読めるようにする。
    case 'generic':
      return post.aiAnalysisGeneric ?? null;
    default: {
      // rawOutput.parsed[modeKey] へ fallback (旧踏襲)
      const raw = post.aiAnalysisRaw;
      if (raw?.parsed && typeof raw.parsed === 'object') {
        const parsed = raw.parsed as Record<string, unknown>;
        const v = parsed[modeKey];
        return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
      }
      return null;
    }
  }
}

function extractRaw(desc: ScoreFieldDescriptor, post: PostMinimal): number[] {
  switch (desc.extractor.kind) {
    case 'extra-field': {
      const v = post.extraFields?.[desc.extractor.key];
      return typeof v === 'number' && Number.isFinite(v) ? [v] : [];
    }
    case 'ai-aggregate': {
      const modeRes = readModeResult(post, desc.extractor.modeKey);
      if (!modeRes) return [];
      const score = modeRes[desc.extractor.field] as Record<string, unknown> | undefined;
      const v = score?.[desc.extractor.sub];
      return typeof v === 'number' && Number.isFinite(v) ? [v] : [];
    }
    case 'ai-classifications-score': {
      const modeRes = readModeResult(post, desc.extractor.modeKey);
      if (!modeRes) return [];
      const list = modeRes.classifications;
      if (!Array.isArray(list)) return [];
      return list
        .map((c) => Number((c as { score?: unknown })?.score))
        .filter((n) => Number.isFinite(n));
    }
    case 'ai-walker-overall': {
      // 【廃止予定 fallback】walker 固定の総合スコア shortcut (legacy 地区の投稿のみ)。
      // 新規地区は M3 aggregateScores.overallKey が aiAnalysis.generic 直下に入るため、
      // `ai.generic.<overallKey>` (ai-scalar) or M5 formula 列で代替する。
      const v = post.aiAnalysisWalker?.walkable_score;
      return typeof v === 'number' && Number.isFinite(v) ? [v] : [];
    }
    case 'ai-scalar': {
      // MD-4: mode result 直下の scalar 数値 (generic の overallKey 等) を読む汎用版。
      const modeRes = readModeResult(post, desc.extractor.modeKey);
      if (!modeRes) return [];
      const v = modeRes[desc.extractor.field];
      return typeof v === 'number' && Number.isFinite(v) ? [v] : [];
    }
  }
}

// ----------------------------
// 抽出条件②（フィルタ） — 旧踏襲、unparseable → 全通し (保守的)
// ----------------------------

const INEQUALITY_RE = /^(==|!=|>=|<=|>|<)\s*(-?\d+(?:\.\d+)?)$/;
const SINGLE_NUM_RE = /^-?\d+(?:\.\d+)?$/;

export function parseFilter(expr: string): (v: number) => boolean {
  const s = expr.trim();
  if (!s) return () => true;

  const m = s.match(INEQUALITY_RE);
  if (m) {
    const op = m[1];
    const n = Number(m[2]);
    switch (op) {
      case '>':
        return (v) => v > n;
      case '<':
        return (v) => v < n;
      case '>=':
        return (v) => v >= n;
      case '<=':
        return (v) => v <= n;
      case '==':
        return (v) => v === n;
      case '!=':
        return (v) => v !== n;
    }
  }

  if (s.includes(',')) {
    const parts = s
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    const nums = parts.map(Number).filter(Number.isFinite);
    if (nums.length > 0 && nums.length === parts.length) {
      const set = new Set(nums);
      return (v) => set.has(v);
    }
  }

  if (SINGLE_NUM_RE.test(s)) {
    const n = Number(s);
    return (v) => v === n;
  }

  if (typeof console !== 'undefined') console.warn('Unparseable score filter:', expr);
  return () => true;
}

// ----------------------------
// 集約 — 旧踏襲
// ----------------------------

function aggregate(values: number[], agg: ScoreAggregator): number | null {
  if (values.length === 0) return null;
  switch (agg) {
    case 'sum':
      return values.reduce((a, b) => a + b, 0);
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'min':
      return Math.min(...values);
    case 'median': {
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      if (sorted.length % 2) return sorted[mid] ?? null;
      const a = sorted[mid - 1] ?? 0;
      const b = sorted[mid] ?? 0;
      return (a + b) / 2;
    }
  }
}

// ----------------------------
// カスタム式パーサ (旧踏襲、再帰下降、eval 不使用)
// Expr := Term (('+'|'-') Term)*
// Term := Factor (('*'|'/') Factor)*
// Factor := '-' Factor | '(' Expr ')' | NumberLiteral | VarRef
// ----------------------------

class ParseError extends Error {}

export function evaluateFormula(formula: string, variables: Record<string, number>): number | null {
  const varNames: string[] = [];
  const replaced = formula.replace(/\[([^\]]+)\]/g, (_m, name: string) => {
    let idx = varNames.indexOf(name);
    if (idx < 0) {
      varNames.push(name);
      idx = varNames.length - 1;
    }
    return `__VAR${idx}`;
  });

  for (const name of varNames) {
    if (!(name in variables)) return null;
    if (!Number.isFinite(variables[name])) return null;
  }

  type Token =
    | { t: 'num'; v: number }
    | { t: 'var'; idx: number }
    | { t: '+' | '-' | '*' | '/' | '(' | ')' };

  const tokens: Token[] = [];
  let i = 0;
  while (i < replaced.length) {
    const ch = replaced[i];
    if (ch === undefined) break;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/' || ch === '(' || ch === ')') {
      tokens.push({ t: ch });
      i++;
      continue;
    }
    if (/\d/.test(ch) || ch === '.') {
      const m = replaced.slice(i).match(/^\d+(\.\d+)?|\.\d+/);
      if (!m) return null;
      tokens.push({ t: 'num', v: Number(m[0]) });
      i += m[0].length;
      continue;
    }
    if (ch === '_') {
      const m = replaced.slice(i).match(/^__VAR(\d+)/);
      if (!m) return null;
      tokens.push({ t: 'var', idx: Number(m[1]) });
      i += m[0].length;
      continue;
    }
    return null; // 未知文字
  }

  let pos = 0;
  const peek = (): Token | undefined => tokens[pos];
  const consume = () => tokens[pos++];

  function parseExpr(): number {
    let v = parseTerm();
    while (pos < tokens.length) {
      const top = peek();
      if (!top) break;
      if (top.t === '+' || top.t === '-') {
        consume();
        const r = parseTerm();
        v = top.t === '+' ? v + r : v - r;
      } else break;
    }
    return v;
  }
  function parseTerm(): number {
    let v = parseFactor();
    while (pos < tokens.length) {
      const top = peek();
      if (!top) break;
      if (top.t === '*' || top.t === '/') {
        consume();
        const r = parseFactor();
        if (top.t === '/') {
          if (r === 0) throw new ParseError('div-by-zero');
          v = v / r;
        } else {
          v = v * r;
        }
      } else break;
    }
    return v;
  }
  function parseFactor(): number {
    if (pos >= tokens.length) throw new ParseError('eof');
    const top = peek();
    if (!top) throw new ParseError('eof');
    if (top.t === '-') {
      consume();
      return -parseFactor();
    }
    if (top.t === '+') {
      consume();
      return parseFactor();
    }
    if (top.t === '(') {
      consume();
      const v = parseExpr();
      const close = peek();
      if (!close || close.t !== ')') throw new ParseError('missing-)');
      consume();
      return v;
    }
    if (top.t === 'num') {
      consume();
      return top.v;
    }
    if (top.t === 'var') {
      consume();
      const name = varNames[top.idx];
      if (name === undefined) throw new ParseError('unknown-var');
      const value = variables[name];
      if (value === undefined) throw new ParseError('undefined-var');
      return value;
    }
    throw new ParseError('unexpected');
  }

  try {
    const result = parseExpr();
    if (pos !== tokens.length) return null;
    if (!Number.isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}

// ----------------------------
// 列単位の評価 (旧踏襲)
// ----------------------------

function findDescriptor(
  fieldPath: string,
  registry: ScoreFieldDescriptor[],
): ScoreFieldDescriptor | null {
  return registry.find((d) => d.path === fieldPath) ?? null;
}

function evaluateAggregateColumn(
  col: ScoreColumnConfig,
  post: PostMinimal,
  registry: ScoreFieldDescriptor[],
): number | null {
  if (col.calculation.kind !== 'aggregate') return null;
  const desc = findDescriptor(col.fieldPath, registry);
  if (!desc) return null;

  const raw = extractRaw(desc, post);
  const sliced =
    desc.cardinality === 'scalar' ? raw : col.selectionMode === 'first' ? raw.slice(0, 1) : raw;

  const filterFn = parseFilter(col.filter.expr);
  const filtered = sliced.filter(filterFn);

  const result = aggregate(filtered, col.calculation.aggregator);
  if (result === null) return null;
  const mult = col.calculation.multiplier;
  return typeof mult === 'number' && Number.isFinite(mult) ? result * mult : result;
}

/**
 * 全列を評価して値配列を返す (旧踏襲、2 段階解決)。
 *  Phase 1: aggregate 列 → label → value マップ
 *  Phase 2: formula 列を `[label]` 参照で評価
 */
export function evaluateAllColumns(
  columns: ScoreColumnConfig[],
  post: PostMinimal,
  registry: ScoreFieldDescriptor[],
): (number | null)[] {
  const aggregateValues: Record<string, number> = {};
  const out: (number | null)[] = new Array(columns.length).fill(null);

  columns.forEach((col, i) => {
    if (col.calculation.kind !== 'aggregate') return;
    const v = evaluateAggregateColumn(col, post, registry);
    out[i] = v;
    if (v !== null && col.label) aggregateValues[col.label] = v;
  });

  columns.forEach((col, i) => {
    if (col.calculation.kind !== 'formula') return;
    out[i] = evaluateFormula(col.calculation.formula, aggregateValues);
  });

  return out;
}

/** 整形 (toFixed + unit suffix、旧踏襲)。 */
export function formatScoreValue(value: number | null, decimals = 1, unit?: string): string {
  if (value === null || !Number.isFinite(value)) return '—';
  const txt = value.toFixed(decimals);
  return unit ? `${txt}${unit}` : txt;
}
